/**
 * Rules for the "just added" cart highlight.
 *
 * Adding a dish that is already in the cart bumps that row's quantity instead
 * of appending, so targeting "the last row" would flash nothing in the most
 * common flow. The caller passes the id of the line that actually changed, and
 * these helpers decide whether to scroll and how long to hold the tint.
 *
 * Kept out of the component so the behaviour is testable without a DOM.
 */

/** How long the green tint stays before fading, in ms. */
export const HIGHLIGHT_DURATION_MS = 1800;

/**
 * Whether a highlight id represents a genuine add worth scrolling to.
 *
 * Guards the two cases that must NOT move the cashier's scroll position:
 *  - no id (removal, quantity decrease, or a plain re-render)
 *  - an id no longer in the cart (the line was removed before the timer ran)
 */
export function shouldScrollToItem(
    highlightItemId: string | null | undefined,
    itemIds: readonly string[],
): boolean {
    if (!highlightItemId) return false;
    return itemIds.includes(highlightItemId);
}

/**
 * Scroll options honouring the viewer's motion preference. Smooth scrolling is
 * disorienting for people who ask for reduced motion, so fall back to an
 * instant jump — the row still comes into view either way.
 */
export function scrollBehaviorFor(prefersReducedMotion: boolean): ScrollIntoViewOptions {
    return {
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
    };
}

/** Reads the media query, tolerating environments without matchMedia (jsdom, SSR). */
export function prefersReducedMotion(win?: { matchMedia?: (q: string) => { matches: boolean } }): boolean {
    const target = win ?? (typeof window === 'undefined' ? undefined : window);
    if (!target?.matchMedia) return false;
    try {
        return target.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
        return false;
    }
}

/** Tailwind classes for the flash. Uses design-system success tokens. */
export function highlightClasses(isHighlighted: boolean): string {
    return isHighlighted
        ? 'bg-success-50 dark:bg-success-900/20'
        : '';
}
